import { useState } from "react";
import axios from "axios";
import { MapPin, Phone, Mail, Clock, ArrowUpRight, CheckCircle2, Loader2, CalendarDays } from "lucide-react";
import Seo from "@/components/Seo";
import { Reveal, Eyebrow } from "@/components/Reveal";
import { services } from "@/data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const contactCards = [
    {
        icon: MapPin,
        title: "Mumbai Office",
        lines: ["Bandra-Kurla Complex (BKC)", "Mumbai, Maharashtra 400051, India"],
        testid: "contact-card-office",
    },
    {
        icon: Phone,
        title: "Phone",
        lines: ["+91 22 4978 3100", "Mon – Fri, 08:30 – 19:30 IST"],
        testid: "contact-card-phone",
    },
    {
        icon: Mail,
        title: "Email",
        lines: ["inquiries@vantagecrestresearch.com", "Confidential reply within 4 business hours"],
        testid: "contact-card-email",
    },
    {
        icon: Clock,
        title: "Book a Consultation",
        lines: ["Schedule a confidential research briefing with a founding partner."],
        cta: true,
        testid: "contact-card-consultation",
    },
];

function MapPlaceholder() {
    return (
        <div
            data-testid="contact-map-placeholder"
            className="relative h-full min-h-[320px] bg-[#171717] grid-lines-dark border border-white/10 flex flex-col justify-between p-7 overflow-hidden"
        >
            <div className="flex items-center justify-between">
                <span className="font-mono-accent text-[10px] tracking-[0.22em] uppercase text-[#D8BC7A]">
                    Google Maps · Embed Placeholder
                </span>
                <span className="font-mono-accent text-[10px] tracking-[0.18em] text-[#77776E]">
                    VCR-HQ
                </span>
            </div>
            <svg viewBox="0 0 400 140" className="w-full h-auto my-6" aria-hidden="true">
                <g stroke="rgba(201,162,75,0.45)" strokeWidth="1" fill="none">
                    <path d="M0 120 h400" />
                    <rect x="30" y="70" width="26" height="50" />
                    <rect x="66" y="50" width="20" height="70" />
                    <rect x="96" y="84" width="30" height="36" />
                    <rect x="136" y="38" width="24" height="82" />
                    <rect x="170" y="62" width="18" height="58" />
                    <rect x="198" y="24" width="28" height="96" />
                    <rect x="236" y="58" width="22" height="62" />
                    <rect x="268" y="76" width="30" height="44" />
                    <rect x="308" y="46" width="20" height="74" />
                    <rect x="338" y="68" width="26" height="52" />
                </g>
                <circle cx="212" cy="24" r="4" fill="#D8BC7A" />
                <circle cx="212" cy="24" r="8" stroke="#C9A24B" strokeWidth="1" fill="none" className="pulse-ring" />
            </svg>
            <div>
                <p className="font-mono-accent text-[10px] tracking-[0.22em] uppercase text-[#A6A69C]">
                    LAT 18.9220 N · LON 72.8346 E
                </p>
                <p className="text-sm text-[#E7E4DC] mt-2">
                    Bandra-Kurla Complex (BKC), Mumbai — India's financial district.
                </p>
            </div>
        </div>
    );
}

export default function Contact() {
    const [form, setForm] = useState({ name: "", email: "", company: "", service: "", message: "" });
    const [status, setStatus] = useState("idle");
    const [errorMsg, setErrorMsg] = useState("");

    const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

    const submit = async (e) => {
        e.preventDefault();
        setStatus("loading");
        setErrorMsg("");
        try {
            await axios.post(`${API}/enquiries`, { ...form, type: "general_inquiry" });
            setStatus("success");
            setForm({ name: "", email: "", company: "", service: "", message: "" });
        } catch (err) {
            setStatus("error");
            setErrorMsg("Something went wrong. Please email us directly at inquiries@vantagecrestresearch.com.");
        }
    };

    const inputCls =
        "w-full bg-[#171717] border border-white/15 focus:border-[#C9A24B] focus:ring-2 focus:ring-[#C9A24B]/30 focus:outline-none text-[#E7E4DC] text-sm px-4 py-3.5 placeholder:text-[#77776E] transition-colors duration-200";

    return (
        <div>
            <Seo
                title="Contact | Vantage Crest Research — Executive Search Mumbai"
                description="Contact Vantage Crest Research in Mumbai, India. Submit a mandate enquiry, register for our talent pool, or book a confidential consultation."
            />

            <section
                className="relative overflow-hidden bg-[#0C0C0C] pt-40 pb-24"
                style={{
                    backgroundImage:
                        "radial-gradient(circle at 80% 20%, rgba(18,18,18,0.8) 0%, rgba(6,6,6,0.95) 55%, #060606 100%)",
                }}
            >
                <div className="absolute inset-0 grid-lines-dark pointer-events-none" aria-hidden="true" />
                <div className="relative max-w-7xl mx-auto px-5 sm:px-8">
                    <Eyebrow dark>Contact</Eyebrow>
                    <h1 className="font-display font-bold text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.08] text-[#F7F5F1] mt-6 max-w-3xl">
                        Start a <span className="text-[#D8BC7A]">confidential</span> conversation.
                    </h1>
                    <p className="mt-8 text-lg text-[#A6A69C] leading-relaxed max-w-2xl">
                        Mandate enquiries, talent pool registrations and research briefings —
                        every message is handled by a founding partner, in confidence.
                    </p>
                </div>
            </section>

            <section className="bg-[#F7F5F1] py-16 border-b border-[#E7E4DC]">
                <div className="max-w-7xl mx-auto px-5 sm:px-8 grid sm:grid-cols-2 lg:grid-cols-4 gap-px bg-[#E7E4DC] border border-[#E7E4DC]">
                    {contactCards.map((c, i) => (
                        <Reveal key={c.title} delay={i * 0.07} className="h-full">
                            <div data-testid={c.testid} className="bg-white p-7 h-full">
                                <c.icon size={20} className="text-[#C9A24B]" strokeWidth={1.5} />
                                <h3 className="mt-4 font-semibold text-[#141414]">{c.title}</h3>
                                {c.lines.map((l, j) => (
                                    <p key={j} className="text-sm text-[#77776E] mt-1.5 leading-relaxed">
                                        {l}
                                    </p>
                                ))}
                                {c.cta && (
                                    <a
                                        href="#consultation"
                                        onClick={(e) => e.preventDefault()}
                                        data-testid="contact-book-consultation-btn"
                                        className="mt-4 inline-flex items-center gap-2 text-sm font-semibold text-[#161616] hover:text-[#C9A24B] transition-colors"
                                    >
                                        <CalendarDays size={15} /> Book a Consultation
                                    </a>
                                )}
                            </div>
                        </Reveal>
                    ))}
                </div>
            </section>

            <section className="bg-[#0C0C0C] py-24">
                <div className="max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-2 gap-10 items-stretch">
                    <Reveal className="h-full">
                        <div className="bg-[#171717]/60 border border-white/10 p-8 sm:p-10 h-full">
                            <Eyebrow dark>Send an Enquiry</Eyebrow>
                            <h2 className="font-display text-2xl sm:text-3xl font-semibold text-[#F6F4EF] mt-4">
                                Tell us about the mandate.
                            </h2>

                            {status === "success" ? (
                                <div
                                    data-testid="contact-submission-success-banner"
                                    className="mt-10 border border-[#C9A24B]/50 bg-[#C9A24B]/10 p-8 text-center"
                                >
                                    <CheckCircle2 size={36} className="mx-auto text-[#D8BC7A]" />
                                    <h3 className="font-display text-xl font-semibold text-[#F6F4EF] mt-4">
                                        Enquiry received.
                                    </h3>
                                    <p className="text-sm text-[#A6A69C] mt-2 leading-relaxed">
                                        Your confidential enquiry has been routed to our practice
                                        partners. Expect a reply within 4 business hours.
                                    </p>
                                    <button
                                        onClick={() => setStatus("idle")}
                                        data-testid="contact-send-another-btn"
                                        className="mt-6 text-sm font-semibold text-[#D8BC7A] hover:text-[#E5CF9C] transition-colors"
                                    >
                                        Send another enquiry
                                    </button>
                                </div>
                            ) : (
                                <form
                                    onSubmit={submit}
                                    data-testid="contact-enquiry-form"
                                    className="mt-8 space-y-5"
                                >
                                    <div className="grid sm:grid-cols-2 gap-5">
                                        <div>
                                            <label htmlFor="contact-name" className="block text-xs font-semibold text-[#A6A69C] mb-2 tracking-wide">
                                                Full Name *
                                            </label>
                                            <input
                                                id="contact-name"
                                                data-testid="contact-input-name"
                                                required
                                                value={form.name}
                                                onChange={update("name")}
                                                placeholder="Your name"
                                                className={inputCls}
                                            />
                                        </div>
                                        <div>
                                            <label htmlFor="contact-email" className="block text-xs font-semibold text-[#A6A69C] mb-2 tracking-wide">
                                                Work Email *
                                            </label>
                                            <input
                                                id="contact-email"
                                                type="email"
                                                data-testid="contact-input-email"
                                                required
                                                value={form.email}
                                                onChange={update("email")}
                                                placeholder="name@company.com"
                                                className={inputCls}
                                            />
                                        </div>
                                    </div>
                                    <div className="grid sm:grid-cols-2 gap-5">
                                        <div>
                                            <label htmlFor="contact-company" className="block text-xs font-semibold text-[#A6A69C] mb-2 tracking-wide">
                                                Company
                                            </label>
                                            <input
                                                id="contact-company"
                                                data-testid="contact-input-company"
                                                value={form.company}
                                                onChange={update("company")}
                                                placeholder="Organisation"
                                                className={inputCls}
                                            />
                                        </div>
                                        <div>
                                            <label htmlFor="contact-service" className="block text-xs font-semibold text-[#A6A69C] mb-2 tracking-wide">
                                                Service of Interest
                                            </label>
                                            <select
                                                id="contact-service"
                                                data-testid="contact-select-service"
                                                value={form.service}
                                                onChange={update("service")}
                                                className={inputCls}
                                            >
                                                <option value="">General enquiry</option>
                                                {services.map((s) => (
                                                    <option key={s.id} value={s.title}>
                                                        {s.title}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                    </div>
                                    <div>
                                        <label htmlFor="contact-message" className="block text-xs font-semibold text-[#A6A69C] mb-2 tracking-wide">
                                            Message *
                                        </label>
                                        <textarea
                                            id="contact-message"
                                            data-testid="contact-input-message"
                                            required
                                            rows={5}
                                            value={form.message}
                                            onChange={update("message")}
                                            placeholder="Tell us about the role, the market, or the intelligence you need."
                                            className={`${inputCls} resize-none`}
                                        />
                                    </div>
                                    {status === "error" && (
                                        <p data-testid="contact-error-banner" className="text-sm text-red-400">
                                            {errorMsg}
                                        </p>
                                    )}
                                    <button
                                        type="submit"
                                        disabled={status === "loading"}
                                        data-testid="contact-submit-button"
                                        className="inline-flex items-center gap-2 bg-[#C9A24B] hover:bg-[#D8BC7A] disabled:opacity-60 text-[#0C0C0C] font-semibold px-8 py-3.5 text-sm transition-colors duration-200 active:scale-[0.98]"
                                    >
                                        {status === "loading" ? (
                                            <>
                                                <Loader2 size={16} className="animate-spin" /> Sending…
                                            </>
                                        ) : (
                                            <>
                                                Submit Enquiry <ArrowUpRight size={16} />
                                            </>
                                        )}
                                    </button>
                                    <p className="text-xs text-[#77776E] leading-relaxed">
                                        Submitted information is handled under strict data
                                        protection and confidentiality protocols.
                                    </p>
                                </form>
                            )}
                        </div>
                    </Reveal>
                    <Reveal delay={0.12} className="h-full">
                        <MapPlaceholder />
                    </Reveal>
                </div>
            </section>
        </div>
    );
}
